/* FSM voucher formats v1.1: confirmed additions open the cart; delivery checks gate checkout. */
(() => {
  'use strict';
  const formats = {
    PDF: {label: 'PDF per E-Mail', cents: 0},
    DIN_LANG: {label: 'Gutschein in DIN-lang-Mappe', cents: 499},
    GIFT_FOLDER: {label: 'Gutschein in Geschenkmappe', cents: 999},
    GIFT_BOX: {label: 'Gutschein in Geschenkbox', cents: 1999}
  };
  const reserved = ['_fsm_delivery_version', '_fsm_delivery', '_fsm_pair', '_fsm_delivery_role'];
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const quantityValid = n => Number.isSafeInteger(n) && n >= 1 && n <= 100;
  const pairValid = p => /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(p || '');
  const idValid = id => /^[1-9]\d{0,29}$/.test(String(id || ''));
  function assertConfig(config) {
    const ids = ['DIN_LANG', 'GIFT_FOLDER', 'GIFT_BOX'].map(key => config?.variants?.[key]);
    if (ids.some(id => !idValid(id)) || new Set(ids.map(String)).size !== 3) throw new Error('Die Gutscheinausführungen sind noch nicht vollständig eingerichtet.');
  }
  function buildItems({id, quantity, properties = {}, format, pair, title}, config) {
    assertConfig(config);
    if (!idValid(id) || !quantityValid(quantity) || !own(formats, format) || !pairValid(pair)) throw new Error('Bitte eine gültige Gutscheinausführung und Stückzahl wählen.');
    const props = Object.fromEntries(Object.entries(properties).filter(([key]) => !reserved.includes(key) && key !== 'Ticketausführung'));
    Object.assign(props, {_fsm_delivery_version: '1', _fsm_delivery: format, _fsm_pair: pair, Ticketausführung: formats[format].label});
    const items = [{id: String(id), quantity, properties: props}];
    if (format !== 'PDF') items.push({id: String(config.variants[format]), quantity, properties: {
      _fsm_delivery_role: 'packaging', _fsm_pair: pair, 'Gehört zu': String(title || 'Fluggutschein').slice(0, 200)
    }});
    return items;
  }
  function cartIssues(cart, config, ticketKeys = [], {checkTicketShipping = true} = {}) {
    const issues = [];
    const configuredIds = Object.values(config?.variants || {}).filter(Boolean).map(String);
    const items = cart.items || [];
    const parents = items.filter(l => l.properties?._fsm_delivery_version || l.properties?._fsm_delivery);
    const children = items.filter(l => l.properties?._fsm_delivery_role || configuredIds.includes(String(l.variant_id)));
    const modern = parents.length || children.length || items.some(l => reserved.some(k => own(l.properties || {}, k)));
    if (!modern && !ticketKeys.length) return issues;
    try { assertConfig(config); } catch (error) { return [error.message]; }
    if (cart.currency && cart.currency !== 'EUR') issues.push('Die Gutscheinausführungen sind derzeit nur in Euro verfügbar.');
    const seen = new Set(), used = new Set();
    for (const parent of parents) {
      const p = parent.properties || {}, pair = p._fsm_pair, format = p._fsm_delivery;
      if (checkTicketShipping && parent.requires_shipping !== false) issues.push('Für diesen Gutschein ist die gewählte Lieferart noch nicht verfügbar. Bitte kontaktiere uns.');
      if (p._fsm_delivery_version !== '1' || p._fsm_delivery_role || !pairValid(pair) || seen.has(pair) || !own(formats, format) || !quantityValid(parent.quantity)) {
        issues.push('Eine Gutscheinausführung ist nicht eindeutig. Bitte diesen Gutschein entfernen und neu auswählen.'); continue;
      }
      seen.add(pair);
      const linked = children.filter(c => c.properties?._fsm_pair === pair);
      if (format === 'PDF') {
        if (linked.length) issues.push('Ein PDF-Gutschein darf keine zusätzliche Verpackung enthalten.');
        continue;
      }
      const child = linked[0];
      if (linked.length !== 1 || String(child.variant_id) !== String(config.variants[format]) ||
          child.properties?._fsm_delivery_role !== 'packaging' || child.quantity !== parent.quantity ||
          child.requires_shipping !== true || child.original_price !== formats[format].cents) {
        issues.push('Verpackung, Preis oder Stückzahl passt nicht zum Gutschein. Bitte das betroffene Set entfernen und neu auswählen.');
      } else used.add(child.key);
    }
    if (children.some(c => !used.has(c.key))) issues.push('Eine Verpackung ist keinem passenden Gutschein zugeordnet. Bitte das betroffene Set entfernen und neu auswählen.');
    if (items.some(l => l.properties?._fsm_pair && !parents.includes(l) && !children.includes(l))) issues.push('Ein Gutschein-Set ist unvollständig. Bitte entfernen und neu auswählen.');
    if (ticketKeys.some(key => !parents.some(p => p.key === key))) issues.push('Ein Gutschein stammt noch aus der bisherigen Auswahl. Bitte entfernen und die gewünschte Ausführung neu auswählen.');
    return [...new Set(issues)];
  }
  function linkedUpdates(cart, key, quantity) {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 100) throw new Error('Bitte eine gültige Stückzahl zwischen 0 und 100 eingeben.');
    const line = cart.items.find(l => l.key === key);
    if (!line) throw new Error('Der Warenkorb hat sich geändert. Bitte die Seite neu laden.');
    const pair = line.properties?._fsm_pair;
    const group = pair ? cart.items.filter(l => l.properties?._fsm_pair === pair) : [line];
    return Object.fromEntries(group.map(l => [l.key, quantity]));
  }
  const api = {buildItems, cartIssues, linkedUpdates, formats};
  globalThis.FSMPackaging = api;
  if (typeof document === 'undefined') return;

  const config = () => JSON.parse(document.getElementById('fsm-packaging-config')?.textContent || '{}');
  const root = () => window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  const pendingQuantities = new Map();
  function enqueue(work) {
    const job = queue.then(work);
    queue = job.catch(() => {});
    return job;
  }
  async function request(endpoint, body) {
    const response = await fetch(root() + endpoint, {method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'}, ...(body ? {body: JSON.stringify(body)} : {})});
    const value = await response.json();
    if (!response.ok || value.status >= 400 || value.errors) {
      const error = new Error(typeof value.description === 'string' ? value.description : 'Der Warenkorb konnte nicht aktualisiert werden. Bitte prüfen, bevor Du erneut hinzufügst.');
      error.cartResponseReceived = true;
      throw error;
    }
    return value;
  }
  function showError(message, form) {
    const ownError = form?.querySelector('.product-form__error-message');
    if (ownError) { ownError.hidden = false; ownError.textContent = message; return; }
    let box = document.getElementById('fsm-packaging-error');
    if (!box) {
      box = document.createElement('div'); box.id = 'fsm-packaging-error'; box.className = 'fsm-packaging-error'; box.setAttribute('role', 'alert');
      document.body.append(box);
    }
    box.replaceChildren(document.createTextNode(message + ' '));
    const link = document.createElement('a'); link.href = root() + 'cart'; link.textContent = 'Warenkorb prüfen'; box.append(link);
    const close = document.createElement('button'); close.type = 'button'; close.textContent = 'Schließen'; close.onclick = () => box.remove(); box.append(close);
  }
  function sections() {
    const result = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {bubbles: true, detail: {sections: result}}));
    return [...new Set(result)].slice(0, 5);
  }
  function publish(cart, extra = {}) {
    window.theme?.pubsub?.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {source: 'fsm-packaging', cart, ...extra});
    if (!cart.sections || Object.values(cart.sections).some(v => !v)) document.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true, detail: {open: false}}));
  }
  function ticketKeys() {
    return config().enabled ? [...new Set([...document.querySelectorAll('[data-fsm-ticket="true"]')].map(e => e.dataset.fsmItemKey))] : [];
  }
  api.active = () => config().enabled || !!document.querySelector('[data-fsm-pair]:not([data-fsm-pair=""])');
  api.submit = (form, event) => {
    event.preventDefault();
    if (form.dataset.fsmBusy === 'true') return;
    if (form.dataset.fsmUncertain === 'true') { location.assign(root() + 'cart'); return; }
    form.dataset.fsmBusy = 'true';
    const button = event.submitter || form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    form.querySelector('.product-form__error-message')?.setAttribute('hidden', '');
    let submitted = false, addConfirmed = false;
    enqueue(async () => {
      const ui = form.querySelector('[data-fsm-format-picker]');
      if (ui?.dataset.ready !== 'true') throw new Error('Die Gutscheinauswahl ist noch nicht bereit. Bitte die Seite neu laden.');
      const data = new FormData(form), properties = {};
      if (data.get('selling_plan')) throw new Error('Gutscheinausführungen können nicht mit einem Abonnement kombiniert werden.');
      for (const [key, value] of data) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (match) {
          if (typeof value !== 'string') throw new Error('Dateianhänge können hier nicht übernommen werden.');
          properties[match[1]] = value;
        }
      }
      const items = buildItems({id: data.get('id'), quantity: Number(data.get('quantity') || 1), properties,
        format: ui.querySelector('input:checked')?.value, pair: crypto.randomUUID(), title: ui.dataset.productTitle}, config());
      // Preserve the theme's optional complementary products; they remain separate accessories.
      for (const bundle of form.bundles || []) items.push({id: bundle.value, quantity: Number(bundle.dataset.min || 1)});
      const bundledSections = sections();
      submitted = true;
      const added = await request('cart/add.js', {items, sections: bundledSections, sections_url: location.pathname});
      addConfirmed = true;
      const cart = await request('cart.js');
      cart.sections = added.sections;
      publish(cart);
      // Shopify has confirmed the addition. Validate the complete order at checkout,
      // rather than misreporting delivery configuration or an older cart as a failed add.
      if (window.theme?.settings?.cartType === 'page' || document.body.classList.contains('template-cart')) { location.assign(root() + 'cart'); return; }
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {detail: {product: added}}));
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.show) drawer.show(button); else location.assign(root() + 'cart');
    }).catch(error => {
      if (submitted) form.dataset.fsmUncertain = 'true';
      // A failed cart refresh or drawer must never turn a confirmed add into an
      // error or prompt a second POST. The cart page can fetch its own fresh state.
      if (addConfirmed) { location.assign(root() + 'cart'); return; }
      showError(submitted && !error.cartResponseReceived ? 'Der Warenkorb konnte nicht bestätigt werden. Bitte prüfe ihn, bevor Du erneut hinzufügst. Mit erneutem Klick öffnest Du den Warenkorb.' : (error.message || 'Die Auswahl konnte nicht übernommen werden.'), form);
      document.dispatchEvent(new CustomEvent('cart:refresh', {bubbles: true, detail: {open: false}}));
    }).finally(() => { delete form.dataset.fsmBusy; button?.removeAttribute('aria-busy'); });
  };
  api.changeQuantity = (component, key, quantity, name, target) => {
    component.enableLoading(key);
    return enqueue(async () => {
      const cart = await request('cart.js');
      const updates = linkedUpdates(cart, key, Number(quantity));
      const line = cart.items.find(l => l.key === key);
      const next = line.properties?._fsm_pair
        ? await request('cart/update.js', {updates, sections: sections(), sections_url: location.pathname})
        : await request('cart/change.js', {id: key, quantity: Number(quantity), sections: sections(), sections_url: location.pathname});
      if (pendingQuantities.get(key) === Number(quantity)) pendingQuantities.delete(key);
      publish(next, {line: key, name, target});
      const issues = cartIssues(next, config(), [], {checkTicketShipping: false});
      if (issues.length) showError(issues.join(' '));
      else document.getElementById('fsm-packaging-error')?.remove();
    }).catch(error => showError(error.message)).finally(() => component.disableLoading(key));
  };
  function initialize() {
    for (const picker of document.querySelectorAll('[data-fsm-format-picker]:not([data-ready])')) {
      try {
        assertConfig(config());
        if (picker.dataset.valid !== 'true') throw new Error('Die Gutscheinauswahl ist derzeit nicht verfügbar. Bitte kontaktiere uns.');
        picker.dataset.ready = 'true';
        picker.querySelectorAll('input[data-available="true"]').forEach(input => { input.disabled = false; });
      } catch (error) { picker.dataset.ready = 'false'; picker.querySelector('[data-fsm-picker-note]').textContent = error.message; }
    }
    for (const row of document.querySelectorAll('[data-fsm-role="packaging"]')) {
      for (const input of row.querySelectorAll('input[name="updates[]"]')) { input.readOnly = true; input.setAttribute('aria-label', 'Menge wird über den Gutschein geändert'); }
      for (const button of row.querySelectorAll('quantity-input button')) button.disabled = true;
    }
  }
  // Capture edits before the theme's 300 ms debounce; checkout must not race it.
  function rememberQuantity(event) {
    const target = event.target;
    if (api.active() && target.matches?.('cart-items input[name="updates[]"][data-index]'))
      pendingQuantities.set(target.dataset.index, Number(target.value));
  }
  document.addEventListener('input', rememberQuantity, true);
  document.addEventListener('change', rememberQuantity, true);
  async function checkoutCart() {
    let cart = await request('cart.js');
    if (pendingQuantities.size) {
      const updates = {};
      for (const [key, quantity] of pendingQuantities) Object.assign(updates, linkedUpdates(cart, key, quantity));
      cart = await request('cart/update.js', {updates, sections: sections(), sections_url: location.pathname});
      pendingQuantities.clear();
      publish(cart);
    }
    return cart;
  }
  // Check the final cart before native checkout. No client-side price rewriting.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!api.active()) return;
    if (!(form instanceof HTMLFormElement) || !event.submitter?.matches('[name="checkout"]') || form.dataset.fsmApproved === 'true') return;
    event.preventDefault(); event.stopImmediatePropagation();
    enqueue(async () => {
      const cart = await checkoutCart();
      const issues = cartIssues(cart, config(), ticketKeys());
      if (issues.length) throw new Error(issues.join(' '));
      const currentForm = form.isConnected ? form : (form.id ? document.getElementById(form.id) : document.querySelector('form.drawer__footer-buttons'));
      const currentButton = event.submitter.isConnected ? event.submitter : currentForm?.querySelector('[name="checkout"]') || document.querySelector('[name="checkout"][form="cart"]');
      if (!currentForm || !currentButton) throw new Error('Der Warenkorb wurde aktualisiert. Bitte erneut zur Kasse gehen.');
      currentForm.dataset.fsmApproved = 'true';
      try { currentForm.requestSubmit(currentButton); } finally { delete currentForm.dataset.fsmApproved; }
    }).catch(error => showError(error.message));
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href*="/checkout"]');
    if (!link || !config().enabled || link.dataset.fsmApproved === 'true') return;
    event.preventDefault(); event.stopImmediatePropagation();
    enqueue(async () => {
      const cart = await checkoutCart();
      const issues = cartIssues(cart, config(), ticketKeys());
      if (issues.length) throw new Error(issues.join(' '));
      location.assign(link.href);
    }).catch(error => showError(error.message));
  }, true);
  let scheduled = false;
  new MutationObserver(() => { if (!scheduled) { scheduled = true; queueMicrotask(() => { scheduled = false; initialize(); }); } }).observe(document.documentElement, {childList: true, subtree: true});
  initialize();
})();
